import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import miaFlowAuth from '../auth/MiaFlowAuth';
import CreateProjectModal from '../components/CreateProjectModal';
import NewProjectDialog from '../components/sandbox/NewProjectDialog';
import ProjectCard from '../components/ProjectCard';
import ConfirmDialog from '../components/ConfirmDialog';
import SharedBadge from '../components/SharedBadge';
import Toast from '../components/Toast';
import Icon from '../components/icons/Icon';
import { Copy, UsersThree } from '@phosphor-icons/react';
import {getProjectThumbnail} from '../utils/screenshotCapture';
import { useAuth } from '../context/AuthContext';
import { clearAllPreviewSessions } from '../components/Canvas';
import './Projects.css';
import '../components/sandbox/sandbox.css';
import { devLog } from '../utils/devLog';
import { projectHomeUrl } from '../utils/openProject';
import config from '../config';
import useSandbox from '../hooks/useSandbox';
import useProjectsRunState from '../hooks/useProjectsRunState';
import { getProjectServices, isRunning } from '../services/SandboxService';
import ProjectRunControls from '../components/sandbox/ProjectRunControls';
import VendorName from '../components/sandbox/VendorName';
import SandboxWaking from '../components/sandbox/SandboxWaking';
import { AWAKE_EVENT } from '../hooks/useSandboxWake';
import { loadListMetadata } from '../utils/projectListMetadata';
import StartButton from '../components/sandbox/StartButton';
import { projectStartKey, runStart } from '../components/sandbox/startRuns';
import { runItems, runSummary, startProject, stopProject } from '../components/sandbox/projectRun';
import useWorkspaces from '../hooks/useWorkspaces';
import { usePhone } from '../hooks/useMediaQuery';
import AddToWorkspaceDialog from '../components/workspaces/AddToWorkspaceDialog';
import { announceWorkspacesChanged } from '../services/WorkspacesService';
import './Workspaces.css';
import { lastActivityAt, lastActivityMs } from '../utils/projectRecency';
import { DEFAULT_FILTERS, SORT_PRESETS, activeFilterCount, loadFilters, matchesSearch, presetOf, saveFilters, sortProjects } from '../utils/projectFilters';
import DataTable, { ColumnsButton, useTableColumns } from '../components/table/DataTable';
import { ARCHIVE_CHANGED, listArchivedProjects, unarchiveProject } from '../services/ProjectArchiveService';
import ArchiveDialog from '../components/projects/ArchiveDialog';
import { ArchivedProjectsList, ProjectArchiveMenu } from '../components/projects/ArchivedProjects';

// The table's hidden columns, remembered in this browser; "Made with" starts hidden (the table keeps to the columns
// that matter first).
const COLUMNS_KEY = 'projects_table_columns';
const seedHiddenColumns = () => {
  try {
    if (window.localStorage.getItem(COLUMNS_KEY) === null) { window.localStorage.setItem(COLUMNS_KEY, JSON.stringify(['madeWith', 'envs'])); }
  } catch (_error) { /* every column shows */ }
};

const Projects = () => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const phone = usePhone();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // One sandbox per person: while the person's sandbox sleeps or wakes, the BFF lists what the index knows (theirs
  // only) and says so (`sandbox.state: 'waking'`); the list is fetched again, quietly, until it is up.
  const [sandboxWaking, setSandboxWaking] = useState(false);
  const wakeRetries = useRef(0);
  const [selectedTenant, setSelectedTenant] = useState(() => miaFlowAuth.getSelectedTenant());
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  // Remix: the project whose copy "New project" makes (its remix mode), or null.
  const [remixOf, setRemixOf] = useState(null);
  const [initialProjectPrompt, setInitialProjectPrompt] = useState('');
  const [toast, setToast] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('projectsViewMode') || 'list');
  // Explicit select mode (matches History/Memories.jsx's own pattern) —
  // checkboxes only render once the user opts in, instead of always-on
  // real estate competing with the favorite star in the same card corner.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  // Archived projects (services/ProjectArchiveService, sandbox-first): hidden from the list; "Archived (n)" shows them
  // instead, each to open, unarchive or delete. `archiving`: the projects the Archive confirmation is open for.
  const archiveOn = Boolean(config.sandboxFirst);
  const [archivedView, setArchivedView] = useState(false);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [archiving, setArchiving] = useState(null);
  const [unarchiving, setUnarchiving] = useState(false);

  // Toast helpers
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };
  
  const hideToast = () => {
    setToast(null);
  };

  // Helper to format dates safely
  // Every call site here is a "last opened" timestamp (visitedAt, falling
  // back to updatedAt/createdAt) — a bare date ("Sep 7, 2026") collapses
  // every visit from today into the same indistinguishable label. Show
  // relative time for anything recent, a same-day clock time otherwise, and
  // only fall back to a full date once it's genuinely a different day.
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—';

      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMin = Math.floor(diffMs / 60000);

      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return `${diffMin} min ago`;

      const isToday = date.toDateString() === now.toDateString();
      const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      if (isToday) return time;

      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const isYesterday = date.toDateString() === yesterday.toDateString();
      if (isYesterday) return `Yesterday, ${time}`;

      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '—';
    }
  };
  
  // Helper to get owner/creator name
  const getOwnerName = (project) => {
    // Shared with me by someone else (mia-flow-bff marks these, docs/project-sharing.md).
    if (project.sharedRole) {
      return project.sharedBy ? `by ${project.sharedBy}` : '—';
    }
    if (user?.sub && project.creatorId === user.sub) {
      return 'Me';
    }
    if (user?.email && (project.creatorEmail === user.email || project.configurationGitPath?.includes(user.email))) {
      return 'Me';
    }
    if (project.creatorName) return project.creatorName;
    if (project.creatorEmail) return project.creatorEmail;
    return '—';
  };
  
  // Helper to format project meta (for subtitle)
  const formatProjectMeta = (project) => {
    const owner = getOwnerName(project);
    const date = formatDate(lastActivityAt(project));
    if (date === '—') {
      return `Created by ${owner}`;
    }
    return `Modified by ${owner}, ${date}`;
  };
  
  // Fetch favorites
  const fetchFavorites = useCallback(async () => {
    if (!selectedTenant?.tenantId) return;

    try {
      const favoritesData = await miaPlatformDataService.getFavorites(selectedTenant.tenantId);
      // Convert array of project IDs to Set
      setFavorites(new Set(favoritesData || []));
    } catch (error) {
      console.error('Error fetching favorites:', error);
      setFavorites(new Set());
    }
  }, [selectedTenant?.tenantId]);

  // Fetch favorites on mount and tenant change
  useEffect(() => {
    // Wait for authentication to be ready before fetching
    if (isAuthLoading) return;
    
    fetchFavorites();
  }, [fetchFavorites, isAuthLoading]);

  // Listen for tenant changes
  useEffect(() => {
    const handleTenantChange = (event) => {
      setSelectedTenant(event.detail.tenant);
    };
    
    window.addEventListener('mia-flow-tenant-changed', handleTenantChange);
    
    return () => {
      window.removeEventListener('mia-flow-tenant-changed', handleTenantChange);
    };
  }, []);


  // Filters and sort (utils/projectFilters.js): they combine, and are remembered in this browser.
  const [filters, setFilters] = useState(() => loadFilters());
  const setFilter = (name, value) => setFilters((current) => ({ ...current, [name]: value }));
  useEffect(() => { saveFilters(filters); }, [filters]);
  const { search: searchQuery, creator: creatorFilter, workspace: workspaceFilter, runningOnly, favoritesOnly, sort } = filters;
  const setSearchQuery = (value) => setFilter('search', value);
  const setCreatorFilter = (value) => setFilter('creator', value);
  const setWorkspaceFilter = (value) => setFilter('workspace', value);
  const setRunningOnly = (value) => setFilters((current) => ({ ...current, runningOnly: typeof value === 'function' ? value(current.runningOnly) : value }));
  const toggleFavoritesOnly = () => setFilters((current) => ({ ...current, favoritesOnly: !current.favoritesOnly }));
  const setSort = (value) => setFilter('sort', value);
  const clearFilters = () => setFilters((current) => ({ ...DEFAULT_FILTERS, sort: current.sort }));

  // Fetch projects
  const fetchProjects = useCallback(async (options) => {
    const quiet = options?.quiet === true;
    try {
      if (!quiet) setLoading(true);
      setError(null);
      
      // Real bug: this used to fetch a single hardcoded page=1/per_page=50
      // and stop — no "Load more", no total count, nothing. Once a tenant
      // has more than 50 projects (easy to hit from vfs test/throwaway
      // projects), this page silently showed only whichever 50 the backend
      // happened to return first. Deleting projects out of that visible-50
      // then made the NEXT fetch's top-50 include projects that were always
      // there beyond the old cutoff, just never fetched before — looking
      // exactly like brand-new projects "arising" from nowhere. This is a
      // project management page (the one place users delete from) — it
      // needs the FULL list, not a feed-style page-at-a-time UI. Loop pages
      // until the backend says there's nothing left, with a hard cap so a
      // backend bug (has_more never going false) can't spin forever.
      const perPage = 100;
      const MAX_PAGES = 50; // 5000 projects — far beyond any real tenant
      let projectsData = [];
      let page = 1;
      let waking = false;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const result = await miaPlatformDataService.getProjects(
          selectedTenant?.tenantId,
          // The search filters in the browser (name, description, owner…): the whole list is loaded anyway.
          null,
          page,
          perPage
        );
        projectsData = projectsData.concat(result.data || []);
        waking = waking || result.sandbox?.state === 'waking';
        if (!result.pagination?.has_more || page >= MAX_PAGES) break;
        page += 1;
      }

      // No dedicated "last visited" timestamp source (recent-projects was
      // removed) — every sort/display use of project.visitedAt on this page
      // already falls back to updatedAt/createdAt when it's null, which is
      // the real, live data this now always resolves to.
      const projectsWithDefaults = projectsData.map(project => ({
        ...project,
        visitedAt: project.visitedAt || null
      }));

      // Display projects immediately without waiting for thumbnails
      setProjects(projectsWithDefaults);
      setSandboxWaking(waking);
      if (!waking) wakeRetries.current = 0;
      setLoading(false);
      
      // Fetch thumbnails asynchronously in the background
      // Return abort controller to allow cleanup
      const thumbnailFetches = projectsData.map(async (project) => {
        try {
          const thumbnail = await getProjectThumbnail(project._id, selectedTenant?.tenantId);
          if (thumbnail) {
            // Update only this specific project's thumbnail
            setProjects(prevProjects =>
              prevProjects.map(p =>
                p._id === project._id ? { ...p, thumbnail } : p
              )
            );
          }
        } catch (error) {
          // Silently ignore thumbnail fetch errors
        }
      });

      // Each project's `.mia/project.json` (vfs-service), merged in the background like the thumbnails above: the
      // list's own record is the BFF's registry, written once at creation, while the name, description and
      // technologies the agent writes as the project takes shape live only in that file (vfsClient.readProjectMetadata,
      // as Canvas and the Dashboard read it). Only the projects the person can read, never a shared one (it would wake
      // its owner's sandbox), a bounded few at a time, and not while their own sandbox wakes: the refetch once it is up
      // asks then (utils/projectListMetadata.js). A missing or unreadable file keeps what the list returned.
      const metadataFetches = waking ? [] : [loadListMetadata(projectsData, {
        onMetadata: (projectId, metadata) => {
          if (!(metadata.name || metadata.description || metadata.technologies?.length)) { return; }
          setProjects(prevProjects =>
            prevProjects.map(p => {
              if (p._id !== projectId) return p;
              return {
                ...p,
                name: metadata.name || p.name,
                description: metadata.description || p.description,
                info: {
                  ...p.info,
                  technologies: metadata.technologies?.length ? metadata.technologies : p.info?.technologies,
                  description: metadata.description || p.info?.description,
                },
              };
            })
          );
        },
      })];

      return [...thumbnailFetches, ...metadataFetches];
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err.message || 'Failed to load projects');
      setLoading(false);
      return [];
    }
  }, [selectedTenant]);

  const loadArchived = useCallback(async () => {
    if (!archiveOn || !user?.sub) { return; }
    try {
      setArchivedProjects(await listArchivedProjects(user.sub));
    } catch (err) {
      console.error('Error fetching archived projects:', err);
    }
  }, [archiveOn, user?.sub]);
  useEffect(() => { loadArchived(); }, [loadArchived]);
  // A project archived or unarchived here or elsewhere (its Home): both lists again, once the changes settle.
  useEffect(() => {
    let timer = null;
    const onChanged = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { loadArchived(); fetchProjects({ quiet: true }); }, 300);
    };
    window.addEventListener(ARCHIVE_CHANGED, onChanged);
    return () => { clearTimeout(timer); window.removeEventListener(ARCHIVE_CHANGED, onChanged); };
  }, [loadArchived, fetchProjects]);
  const toggleArchivedView = () => {
    if (!archivedView) { loadArchived(); }
    setArchivedView((current) => !current);
    setSelectionMode(false);
    clearSelection();
  };
  const handleUnarchive = async (ids) => {
    setUnarchiving(true);
    const results = await Promise.allSettled(ids.map((id) => unarchiveProject(id)));
    setUnarchiving(false);
    const failures = results.filter((r) => r.status === 'rejected');
    const done = ids.filter((id, i) => results[i].status === 'fulfilled');
    setArchivedProjects((current) => current.filter((p) => !done.includes(p._id)));
    clearSelection();
    if (failures.length === 0) {
      showToast(ids.length === 1 ? 'Unarchived: it is back in your projects' : `Unarchived ${ids.length} projects`);
    } else {
      showToast(failures[0].reason?.message || `Failed to unarchive ${failures.length} project${failures.length !== 1 ? 's' : ''}`, 'error');
    }
  };
  // Delete from the Archived view: the page's own delete confirmation, for these projects.
  const handleArchivedDelete = (ids) => {
    setSelectedIds(new Set(ids));
    setShowBulkDeleteConfirm(true);
  };
  const askArchive = (list) => setArchiving(list.map((p) => ({ id: p._id, name: p.name || p._id })));
  const onArchived = ({ archived, failed }) => {
    setArchiving(null);
    clearSelection();
    if (archived.length) { setProjects((current) => current.filter((p) => !archived.includes(p._id))); }
    if (failed.length) {
      showToast(failed.length === 1 ? `${failed[0].name}: ${failed[0].error}` : `${failed.length} projects: ${failed[0].error}`, 'error');
    } else if (archived.length) {
      showToast(archived.length === 1 ? 'Archived: find it under Archived' : `Archived ${archived.length} projects`);
    }
  };

  // The sandbox woke (useSandboxWake saw the wake end): fetch again now, quietly, instead of at the next tick.
  useEffect(() => {
    const onAwake = () => { fetchProjects({ quiet: true }); };
    window.addEventListener(AWAKE_EVENT, onAwake);
    return () => window.removeEventListener(AWAKE_EVENT, onAwake);
  }, [fetchProjects]);

  // The sandbox is waking: fetch again every few seconds (about two minutes at most) until the BFF says it is up.
  useEffect(() => {
    if (!sandboxWaking || wakeRetries.current >= 30) return undefined;
    const timer = setTimeout(() => {
      wakeRetries.current += 1;
      fetchProjects({ quiet: true });
    }, 4000);
    return () => clearTimeout(timer);
  }, [sandboxWaking, projects, fetchProjects]);

  // The first load, and again when the organization changes (the search no longer asks the server).
  useEffect(() => {
    // Wait for authentication to be ready before fetching
    if (isAuthLoading) {
      return;
    }
    
    let cancelled = false;
    
    const timeoutId = setTimeout(() => {
      const loadProjects = async () => {
        const fetches = await fetchProjects();
        // Wait for thumbnails only if not cancelled
        if (!cancelled && fetches) {
          await Promise.allSettled(fetches);
        }
      };
      
      loadProjects();
    }, 0);
    
    // Listen for thumbnail updates from Canvas
    const handleThumbnailUpdate = (event) => {
      const { projectId, thumbnailUrl } = event.detail;
      setProjects(prevProjects => 
        prevProjects.map(p => 
          p._id === projectId ? { ...p, thumbnail: thumbnailUrl } : p
        )
      );
    };
    
    window.addEventListener('thumbnail-updated', handleThumbnailUpdate);
    
    // Cleanup: cancel pending requests and clear timeout
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      window.removeEventListener('thumbnail-updated', handleThumbnailUpdate);
    };
  }, [fetchProjects, isAuthLoading]);

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
  };

  // Handle refresh
  const handleRefresh = async () => {
    await fetchProjects();
  };

  // Handle create project click
  //
  // Lazy tenant resolution (same fallback chain as App.jsx's
  // handleStartChatFromHome, and the same "attempt the action, resolve as you
  // go" pattern mia-flow-cli's `/agent` uses): never hard-block the user with
  // a blanket "select an organization first" alert when a reasonable
  // resolution (their own first/only tenant) is available. Only surface a
  // specific error, at the point of actually needing a tenant, if resolution
  // itself fails.
  const handleCreateProjectClick = async (prompt = '') => {
    // Sandbox-first: the one-field "New project" dialog, which resolves the tenant itself (no organization choice).
    if (config.sandboxFirst) {
      setIsCreateProjectModalOpen(true);
      return;
    }

    // Ensure prompt is always a string
    const promptStr = typeof prompt === 'string' ? prompt : '';

    let tenant = selectedTenant;

    if (!tenant) {
      try {
        const tenantsData = await miaPlatformDataService.getTenants();
        const formatted = (tenantsData || []).map(t => miaPlatformDataService.formatTenant(t));
        if (formatted.length > 0) {
          tenant = formatted[0];
          setSelectedTenant(tenant);
          miaFlowAuth.setSelectedTenant(tenant);
        }
      } catch (error) {
        console.error('Failed to auto-resolve an organization for project creation:', error);
      }
    }

    if (!tenant) {
      showToast('Could not find an organization to create the project in. Please try again or select one manually.', 'error');
      return;
    }

    setInitialProjectPrompt(promptStr);
    setIsCreateProjectModalOpen(true);
  };

  // `/projects?new=1` (the sandbox home's "New project") opens the create dialog once, then drops the parameter.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') !== '1') { return; }
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
    handleCreateProjectClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Expose handleCreateProjectClick globally for other components
  useEffect(() => {
    window.miaFlowCreateProject = handleCreateProjectClick;
    return () => {
      delete window.miaFlowCreateProject;
    };
  }, [selectedTenant]);

  // Toggle view mode
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('projectsViewMode', mode);
  };

  // Handle favorite toggle
  const handleFavoriteToggle = async (projectId, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    // Optimistic update
    const newFavorites = new Set(favorites);
    const wasFavorited = newFavorites.has(projectId);
    if (wasFavorited) {
      newFavorites.delete(projectId);
    } else {
      newFavorites.add(projectId);
    }
    setFavorites(newFavorites);

    try {
      await miaPlatformDataService.toggleFavorite(projectId);
      showToast(
        wasFavorited ? 'Removed from favorites' : 'Added to favorites',
        'success'
      );
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Revert on error
      setFavorites(favorites);
      showToast('Failed to update favorite status', 'error');
    }
  };

  // Bulk-select handlers
  const handleSelectToggle = (projectId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedIds((prev) => {
      // If everything currently displayed is already selected, clear —
      // otherwise select every currently-displayed (filtered/sorted) project.
      const allSelected = shownProjects.length > 0 && shownProjects.every((p) => prev.has(p._id));
      if (allSelected) return new Set();
      return new Set(shownProjects.map((p) => p._id));
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => {
      if (prev) clearSelection(); // leaving select mode: drop any selection too
      return !prev;
    });
  };

  const handleBulkDeleteClick = () => {
    if (selectedIds.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const handleBulkDeleteConfirm = async () => {
    setShowBulkDeleteConfirm(false);
    setIsBulkDeleting(true);
    const ids = Array.from(selectedIds);
    // Real bug: firing every delete at once (Promise.allSettled over the
    // full list) sent up to 96 concurrent DELETEs for one user, which raced
    // the BFF's per-user project registry (a single shared JSON file) hard
    // enough that only a fraction of the deletes actually stuck and the
    // list came back empty afterward — see project-store.js's own fix
    // (a per-user write lock) for the real root cause. That server-side
    // fix means concurrent requests are safe now, but firing dozens at
    // once is still unnecessary load — a small, fixed concurrency keeps
    // this fast without hammering the backend.
    const CONCURRENCY = 5;
    const results = [];
    for (let i = 0; i < ids.length; i += CONCURRENCY) {
      const batch = ids.slice(i, i + CONCURRENCY);
      const batchResults = await Promise.allSettled(
        batch.map((id) => miaPlatformDataService.deleteProject(id))
      );
      results.push(...batchResults);
    }
    const failures = results.filter((r) => r.status === 'rejected');
    setIsBulkDeleting(false);
    clearSelection();
    await fetchProjects();
    if (archivedView) { loadArchived(); }
    if (failures.length === 0) {
      setSelectionMode(false);
      showToast(`Deleted ${ids.length} project${ids.length !== 1 ? 's' : ''}`, 'success');
    } else if (failures.length === ids.length) {
      showToast(`Failed to delete ${failures.length} project${failures.length !== 1 ? 's' : ''}`, 'error');
    } else {
      showToast(
        `Deleted ${ids.length - failures.length} of ${ids.length} projects — ${failures.length} failed`,
        'error'
      );
    }
  };

  // Handle create project submission
  const handleCreateProject = async (projectData) => {
    try {
      // Call the service to create project via BFF
      const result = await miaPlatformDataService.createProject(projectData);

      // Refresh projects list
      await fetchProjects();
      
      // Show success toast
      showToast(`🎉 Project "${projectData.projectName}" created successfully!`, 'success');
      
      // Navigate to chat with the new project
      const tenantId = projectData.tenantId;
      const projectId = result.project?._id;
      
      if (tenantId && projectId) {
        // Small delay to let user see the toast
        setTimeout(() => {
          // Set flag to prevent URL reconstruction (new project = no service selected)
          sessionStorage.setItem('mia_switching_to_project', projectId);
          navigate(`/code/${tenantId}/${projectId}`);
        }, 1000);
      } else {
        // Fallback to chat root if we don't have the IDs
        setTimeout(() => {
          navigate('/code');
        }, 1000);
      }
    } catch (error) {
      console.error('Error creating project:', error);
      throw error; // Re-throw to let modal handle it
    }
  };

  const navigate = useNavigate();

  // Sandbox-first (docs/SANDBOX_FIRST.md): which services of each project run now, and a "Running only" filter.
  // Each visible project's own state comes from the pod it runs in (its owner's, per person; loaded lazily, a few at a
  // time: useProjectsRunState); until it is loaded, what the caller's own sandbox overview knows of it.
  const { sandbox } = useSandbox({ enabled: Boolean(config.sandboxFirst), intervalMs: 10000 });
  const projectRun = useProjectsRunState({ enabled: Boolean(config.sandboxFirst) });
  const [bulkRun, setBulkRun] = useState(null); // 'start' | 'stop' while a bulk start/stop goes on
  const runState = (projectId) => {
    if (projectRun.states[projectId]) { return runSummary(projectRun.states[projectId]); }
    const own = (sandbox?.services || []).filter((service) => service.projectId === projectId);
    return { running: own.filter(isRunning).length, total: own.length, crashed: own.some((service) => service.crashed || service.buildStatus === 'failed') };
  };
  // The list's own refresh (a new load of the projects) refreshes the visible projects' state too.
  const { refreshAll: refreshRunStates } = projectRun;
  useEffect(() => { refreshRunStates(); }, [projects, refreshRunStates]);
  // "Running only" needs every project's state, not only the visible ones': the missing ones are queued (a few at a time).
  const { refresh: refreshRunState, states: runStates } = projectRun;
  useEffect(() => {
    if (!runningOnly) { return; }
    projects.forEach((p) => { if (!(p._id in runStates)) { refreshRunState(p._id); } });
  }, [runningOnly, projects, runStates, refreshRunState]);

  /**
   * Bulk Start / Stop of the selected projects. Start asks for every project's services at once and follows them all
   * until they run: the button says "Starting 2/7…" with each project's services in its list, then which did not start.
   * A plan's running-services limit refuses the ones past it (its message once). Stop goes one project after the other.
   */
  const [bulkProgress, setBulkProgress] = useState(null); // { [projectId]: progress } of the last bulk start
  const bulkAlive = useRef(true);
  useEffect(() => {
    bulkAlive.current = true;
    return () => { bulkAlive.current = false; };
  }, []);
  const handleBulkRun = async (action, only = null) => {
    setBulkRun(action);
    if (action === 'start') { setBulkProgress(null); }
    try {
      const ids = only ? Object.keys(only) : Array.from(selectedIds);
      const answers = await Promise.all(ids.map(async (projectId) => [projectId, projectRun.states[projectId] || await getProjectServices(projectId).catch(() => null)]));
      const runnable = answers.filter(([, answer]) => answer && answer.canRun !== false);
      if (action === 'start') {
        const all = {};
        const errors = await Promise.all(runnable.map(async ([projectId, answer]) => {
          const items = runItems(answer).filter((item) => !only || only[projectId].has(item.serviceName));
          // Under the project's own key too: its row shows the same start (and it goes on if this page is left).
          const result = await runStart(projectStartKey(projectId), (onProgress) => startProject(projectId, items, {
            onEach: () => projectRun.refresh(projectId),
            onProgress: (next) => {
              onProgress(next);
              all[projectId] = next;
              if (bulkAlive.current) { setBulkProgress({ ...all }); }
            },
          }));
          projectRun.refresh(projectId);
          return result?.progress?.error || null;
        }));
        const firstError = errors.find(Boolean);
        if (firstError && bulkAlive.current) { showToast(firstError, 'error'); }
        return;
      }
      for (const [projectId, answer] of runnable) {
        // eslint-disable-next-line no-await-in-loop
        const { error: failed } = await stopProject(projectId, runItems(answer));
        projectRun.refresh(projectId);
        if (failed) { showToast(failed, 'error'); }
      }
    } finally {
      if (bulkAlive.current) { setBulkRun(null); }
    }
  };
  // All the bulk start's services as one progress (names prefixed with their project's), for its button.
  const bulkStartProgress = bulkProgress ? {
    services: Object.entries(bulkProgress).flatMap(([projectId, progress]) => progress.services.map((service) => ({
      ...service, serviceName: `${projectId} · ${service.serviceName.replace(/^services\//, '')}`, projectId, name: service.serviceName,
    }))),
    error: Object.values(bulkProgress).map((progress) => progress.error).find(Boolean) || null,
    done: Object.values(bulkProgress).every((progress) => progress.done),
  } : null;
  const retryBulkStart = () => {
    const only = {};
    (bulkStartProgress?.services || []).filter((service) => service.phase !== 'running').forEach((service) => {
      only[service.projectId] = only[service.projectId] || new Set();
      only[service.projectId].add(service.name);
    });
    handleBulkRun('start', only);
  };
  // `startType`: a card's Start is primary; a list row's is a light default button, like its Stop and Open app.
  const runControls = (projectId, startType = 'primary') => (config.sandboxFirst ? (
    <ProjectRunControls
      onChanged={projectRun.refresh}
      onHidden={projectRun.unwant}
      onVisible={projectRun.want}
      projectId={projectId}
      startType={startType}
      state={projectRun.states[projectId] || undefined}
    />
  ) : null);

  // Workspaces (vfs-service docs/workspaces.md, sandbox-first): a filter by workspace (a project in several matches any
  // of them), a chip per workspace next to its name, and "Add to workspace…" on the person's own projects. Nothing shows
  // where the gateway has none.
  const { workspaces, available: workspacesOn, workspacesOf, reload: reloadWorkspaces } = useWorkspaces({ enabled: Boolean(config.sandboxFirst) });
  const [moving, setMoving] = useState(null); // the project "Add to workspace…" is open for
  const inWorkspaceFilter = (project) => {
    if (workspaceFilter === 'all') { return true; }
    const inThem = workspacesOf(project._id);
    return workspaceFilter === 'none' ? inThem.length === 0 : inThem.some((workspace) => workspace.id === workspaceFilter);
  };
  const workspaceTag = (project) => {
    const inThem = workspacesOn ? workspacesOf(project._id) : [];
    return inThem.length > 0 ? (
      <span aria-label={`In ${inThem.length === 1 ? 'the workspace' : 'the workspaces'} ${inThem.map((workspace) => workspace.name).join(', ')}`} className="project-workspace-chips" role="list">
        {inThem.map((workspace) => (
          <span className="sbx-pill project-workspace-tag" data-testid="project-workspace" key={workspace.id} role="listitem" title={`In the workspace ${workspace.name}`}>{workspace.name}</span>
        ))}
      </span>
    ) : null;
  };
  // The creator filter: "me", or one other person by the name the list shows (shared projects count under who shared them).
  const creatorOf = (project) => (project.sharedRole ? (project.sharedBy || '') : getOwnerName(project));
  const inCreatorFilter = (project) => {
    if (creatorFilter === 'all') { return true; }
    const creator = creatorOf(project);
    return creatorFilter === 'me' ? creator === 'Me' : creator === creatorFilter;
  };
  const otherCreators = [...new Set(projects.map(creatorOf).filter((name) => name && name !== 'Me' && name !== '—'))].sort((a, b) => a.localeCompare(b));
  // A remembered creator nobody in the list has now still shows in the dropdown (it filters everything out).
  const creatorOptions = creatorFilter !== 'all' && creatorFilter !== 'me' && !otherCreators.includes(creatorFilter)
    ? [...otherCreators, creatorFilter] : otherCreators;
  const workspaceButton = (project) => (workspacesOn && !project.sharedRole ? (
    <button
      aria-label={`Add ${project.name || 'this project'} to a workspace`}
      className="list-favorite-button project-workspace-button"
      data-testid="project-add-workspace"
      onClick={(e) => { e.stopPropagation(); setMoving(project); }}
      title="Add to workspace…"
      type="button"
    >
      <UsersThree aria-hidden size={16} />
    </button>
  ) : null);

  // "Remix": a copy of a project you can see (yours or shared with you) as a new project of yours (sandbox-first).
  const remixButton = (project) => (config.sandboxFirst ? (
    <button
      aria-label={`Remix ${project.name || 'this project'}`}
      className="list-favorite-button project-remix-button"
      data-testid="project-remix"
      onClick={(e) => { e.stopPropagation(); setRemixOf(project._id); }}
      title="Remix: make your own copy of it"
      type="button"
    >
      <Copy aria-hidden size={16} />
    </button>
  ) : null);

  // Sort projects. This page used to also filter down to a "starred" view
  // when reached via /projects/starred (a nav-submenu item), but that
  // submenu — and its sibling, an already-non-functional "Shared with me"
  // route — has been removed: Projects now shows everything directly, one
  // click from the sidebar, no intermediate view to pick first.
  // The list after the filters (they combine: AND), in the one sort the grid and the table share.
  // A workspace filter the person can no longer pick (the workspace is gone, or none load here) does not filter.
  const workspaceFilterOn = workspaceFilter === 'none'
    ? workspacesOn
    : workspaceFilter !== 'all' && workspaces.some((workspace) => workspace.id === workspaceFilter);
  const searchFields = (project) => [
    project.name, project.description, project.info?.description, project._id, creatorOf(project), getOwnerName(project),
    project.creatorEmail, ...(workspacesOn ? workspacesOf(project._id).map((workspace) => workspace.name) : []),
  ];
  // Status as a rank (all running, partly, stopped with an error, stopped), so "Running first" sorts on it.
  const statusRank = (project) => {
    const state = runState(project._id);
    if (state.running && state.running >= state.total) { return 3; }
    if (state.running) { return 2; }
    return state.crashed ? 1 : 0;
  };
  const sortValues = { status: statusRank, owner: (project) => getOwnerName(project), madeWith: (project) => project.createdWith || '' };
  const displayProjects = sortProjects(projects
    .filter((p) => matchesSearch(searchQuery, searchFields(p)))
    .filter((p) => !favoritesOnly || favorites.has(p._id))
    .filter((p) => !runningOnly || runState(p._id).running > 0)
    .filter((p) => !workspaceFilterOn || inWorkspaceFilter(p))
    .filter(inCreatorFilter), sort, sortValues);
  const filtersOn = activeFilterCount({ ...filters, workspace: workspaceFilterOn ? workspaceFilter : 'all' }) > 0;

  // The table (DataTable): Name about a quarter, Description what the others leave, then Status, Owner, Last visited
  // and Made with (hidden until asked for, Columns ▾), the actions sticky on the right. Every column sorts, with the
  // Sort by presets on the same sort.
  const statusPill = (project) => {
    const state = runState(project._id);
    if (state.running && state.running >= state.total) { return <span className="sbx-pill sbx-pill-running"><span aria-hidden="true" className="sbx-dot" />Running</span>; }
    if (state.running) { return <span className="sbx-pill sbx-pill-running"><span aria-hidden="true" className="sbx-dot" />Partly running {state.running}/{state.total}</span>; }
    if (state.crashed) { return <span className="sbx-pill sbx-pill-crashed">Stopped with an error</span>; }
    return <span className="sbx-pill">Stopped</span>;
  };
  const descriptionOf = (project) => project.description || project.info?.description || '';
  const tableColumns = [
    {
      key: 'name',
      label: 'Name',
      // On a phone the name takes what the actions leave (2026-10-31: 25% left it no room next to Start).
      width: phone ? undefined : '25%',
      value: (project) => project.name || '',
      clip: false,
      render: (project) => (
        <div className="project-name-cell">
          <button
            aria-label={favorites.has(project._id) ? `Remove ${project.name || 'this project'} from favorites` : `Add ${project.name || 'this project'} to favorites`}
            aria-pressed={favorites.has(project._id)}
            className={`list-favorite-button ${favorites.has(project._id) ? 'active' : ''}`}
            onClick={(e) => handleFavoriteToggle(project._id, e)}
            title={favorites.has(project._id) ? 'Remove from favorites' : 'Add to favorites'}
            type="button"
          >
            {favorites.has(project._id) ? '★' : '☆'}
          </button>
          {project.thumbnail ? (
            <img src={project.thumbnail} alt="" className="project-icon" />
          ) : (
            <div aria-hidden="true" className="project-icon-placeholder">
              {project.name?.substring(0, 2).toUpperCase() || 'PR'}
            </div>
          )}
          <div className="project-list-info">
            {/* A button, so a keyboard opens the project too (its click reaches the row, which opens it). */}
            <button className="project-list-name" title={project.name || 'Untitled Project'} type="button">{project.name || 'Untitled Project'}</button>
            {(project.sharedRole || (workspacesOn && workspacesOf(project._id).length > 0)) && (
              <span className="project-list-tags">
                {project.sharedRole && <SharedBadge role={project.sharedRole} />}
                {workspaceTag(project)}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      value: descriptionOf,
      title: (project) => descriptionOf(project) || undefined,
      render: (project) => <span className="project-list-description">{descriptionOf(project) || '—'}</span>,
    },
    config.sandboxFirst ? {
      key: 'status',
      label: 'Status',
      width: 150,
      value: statusRank,
      render: statusPill,
    } : {
      key: 'tech',
      label: 'Tech',
      width: 150,
      value: (project) => (project.info?.technologies || []).join(', '),
      render: (project) => {
        const technologies = project.info?.technologies || [];
        return technologies.length > 0 ? (
          <span className="tech-badges-compact">
            {technologies.slice(0, 2).map((tech) => <span key={tech} className="tech-badge-compact">{tech}</span>)}
            {technologies.length > 2 && <span className="tech-more">+{technologies.length - 2}</span>}
          </span>
        ) : '—';
      },
    },
    { key: 'owner', label: 'Owner', width: 130, value: (project) => getOwnerName(project) },
    {
      key: 'visited',
      label: 'Last visited',
      width: 120,
      value: (project) => lastActivityMs(project) || null,
      title: (project) => (lastActivityMs(project) ? new Date(lastActivityMs(project)).toLocaleString() : undefined),
      render: (project) => formatDate(lastActivityAt(project)),
    },
    config.sandboxFirst ? {
      key: 'madeWith',
      label: 'Made with',
      width: 110,
      value: (project) => project.createdWith || '',
      render: (project) => (project.createdWith ? <VendorName name={project.createdWith} /> : '—'),
    } : {
      key: 'envs',
      label: 'Environments',
      width: 110,
      value: (project) => (project.environments || []).length,
      render: (project) => {
        const count = (project.environments || []).length;
        return count > 0 ? <span className="env-count">{count} env{count !== 1 ? 's' : ''}</span> : '—';
      },
    },
  ];
  seedHiddenColumns();
  const tableColumnState = useTableColumns(COLUMNS_KEY, tableColumns);
  const slots = (config.sandboxFirst ? 1 : 0) + (workspacesOn ? 1 : 0) + (archiveOn ? 1 : 0);
  // The run controls: Start / Stop and Open app side by side (200px). On a phone only the run button fits beside the
  // name; the row's ⋯ keeps the rest.
  const actionsWidth = phone ? 104 : (config.sandboxFirst ? 200 : 0) + slots * 32 + 16;
  // Under this the table scrolls sideways (Name and Description at least 200px each, the others their width).
  const tableMinWidth = tableColumnState.shown.reduce((sum, column) => sum + (typeof column.width === 'number' ? column.width : 200), 0)
    + actionsWidth + (selectionMode ? 40 : 0);
  const sortPreset = presetOf(sort);
  const sortColumn = tableColumns.find((column) => column.key === sort.key);
  const sortLabel = sortColumn ? `${sortColumn.label} ${sort.dir === 'asc' ? '↑' : '↓'}` : 'Custom';

  // The Archived view: the archived projects matching the search, by name or most recently archived first.
  const archivedShown = archivedProjects
    .filter((p) => matchesSearch(searchQuery, [p.name, p.description, p._id]))
    .sort((a, b) => (sort.key === 'name' ? (a.name || '').localeCompare(b.name || '') : String(b.archivedAt || '').localeCompare(String(a.archivedAt || ''))));
  const shownProjects = archivedView ? archivedShown : displayProjects;
  // "⋯ › Archive…": on the person's own projects.
  const archiveMenu = (project) => (archiveOn && !project.sharedRole ? (
    <ProjectArchiveMenu name={project.name} onArchive={() => askArchive([project])} />
  ) : null);

  // Names that appear on more than one card get a secondary identifier
  // shown on the card (see ProjectCard's `showIdHint` prop) — otherwise two
  // same-named projects (a real, observed case) are indistinguishable at a
  // glance in the grid.
  const duplicateNameProjectIds = (() => {
    const countByName = {};
    displayProjects.forEach((p) => {
      const name = (p.name || '').trim().toLowerCase();
      if (!name) return;
      countByName[name] = (countByName[name] || 0) + 1;
    });
    const ids = new Set();
    displayProjects.forEach((p) => {
      const name = (p.name || '').trim().toLowerCase();
      if (name && countByName[name] > 1) ids.add(p._id);
    });
    return ids;
  })();

  // Handle project click - navigate to chat with project loaded
  const handleProjectClick = (project) => {
    const tenantId = selectedTenant?.tenantId || project.tenantId;
    const projectId = project._id;

    if (!projectId) {
      console.error('Missing project ID', { tenantId, projectId });
      return;
    }
    // No organization picked in this browser yet (a fresh one, or after per-person sandboxes, whose listing names
    // none): /open/:projectId resolves the person's organization the way the shared-project email link does.
    if (!tenantId) {
      navigate(`/open/${encodeURIComponent(projectId)}`);
      return;
    }
    
    devLog('🎯 Navigating to project:', { tenantId, projectId, projectName: project.name });

    // Clear ALL project-specific localStorage when navigating to different project
    // IMPORTANT: Clear BEFORE navigation to prevent race conditions with URL reconstruction
    // eslint-disable-next-line no-console
    devLog('🧹 Clearing all project-specific localStorage for project switch');
    localStorage.removeItem('mia_selectedResource');
    localStorage.removeItem('mia_selectedRevision');
    localStorage.removeItem('mia_activeTab');
    localStorage.removeItem('mia_fullProjectConfig');
    localStorage.removeItem('mia_selectedSession');
    
    // Set flag with target projectId to prevent URL reconstruction until project is loaded
    sessionStorage.setItem('mia_switching_to_project', projectId);
    
    // Clear all preview-session-* keys from localStorage
    // Note: This keeps render sessions alive so they can be reused when returning
    clearAllPreviewSessions();
    
    // Open the project on its Dashboard (no service picked yet)
    navigate(projectHomeUrl(tenantId, projectId));
  };

  return (
    <div className="projects-page">
      {/* Toast Notification */}
      {toast && (
        <div className="toast-container">
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={hideToast}
          />
        </div>
      )}
      
      <div className="projects-header">
        <div className="projects-title-section">
          <div className="projects-title-row">
            <Icon color="#3B82F6" glow={false} name="folder" size={22} />
            <h1 className="projects-title">Projects</h1>
          </div>
          <button
            type="button"
            className="projects-new-btn"
            onClick={() => handleCreateProjectClick()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginLeft: 12,
              padding: '6px 14px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--accent-primary, #6366f1)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
          >
            <svg fill="none" height="14" style={{ flexShrink: 0 }} viewBox="0 0 16 16" width="14">
              <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
            </svg>
            New Project
          </button>
        </div>

        <p className="projects-subtitle">
          Your workspaces — each one a git-backed project you can chat with, edit, and preview.
        </p>

        {/* One row on a wide screen: search (grows) · filters · sort · view, refresh, Columns, Select (on the right).
            Narrower, the search takes a row of its own and each group wraps whole. */}
        <div className="projects-controls" data-testid="projects-toolbar">
          <div className="projects-search" data-group="search">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="search-icon" aria-hidden="true">
              <path d="M7 12.5C10.0376 12.5 12.5 10.0376 12.5 7C12.5 3.96243 10.0376 1.5 7 1.5C3.96243 1.5 1.5 3.96243 1.5 7C1.5 10.0376 3.96243 12.5 7 12.5Z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M14.5 14.5L11 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              aria-label="Search projects"
              type="search"
              placeholder="Search name, description, owner…"
              value={searchQuery}
              onChange={handleSearch}
              className="projects-search-input"
            />
          </div>

          <div aria-label="Filters" className="projects-toolbar-group projects-filters" data-group="filters" role="group">
            <select
              aria-label="Creator"
              className="filter-dropdown"
              value={creatorFilter}
              onChange={(e) => setCreatorFilter(e.target.value)}
            >
              <option value="all">All creators</option>
              <option value="me">Me</option>
              {creatorOptions.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>

            {workspacesOn && workspaces.length > 0 && (
              <select
                aria-label="Workspace"
                className="filter-dropdown"
                onChange={(e) => setWorkspaceFilter(e.target.value)}
                value={workspaceFilterOn ? workspaceFilter : 'all'}
              >
                <option value="all">All workspaces</option>
                <option value="none">No workspace</option>
                {workspaces.map((workspace) => <option key={workspace.id} value={workspace.id}>{workspace.name}</option>)}
              </select>
            )}

            {config.sandboxFirst && (
              <button
                aria-pressed={runningOnly}
                className={`favorites-filter-btn ${runningOnly ? 'active' : ''}`}
                onClick={() => setRunningOnly((value) => !value)}
                title={runningOnly ? 'Show all projects' : 'Show only projects with a running service'}
                type="button"
              >
                Running only
              </button>
            )}

            <button
              aria-pressed={favoritesOnly}
              className={`favorites-filter-btn ${favoritesOnly ? 'active' : ''}`}
              onClick={toggleFavoritesOnly}
              title={favoritesOnly ? 'Show all projects' : 'Show only favorites'}
              type="button"
            >
              <span aria-hidden="true">{favoritesOnly ? '★' : '☆'}</span> Favorites
            </button>

            {archiveOn && (archivedProjects.length > 0 || archivedView) && (
              <button
                aria-pressed={archivedView}
                className={`favorites-filter-btn ${archivedView ? 'active' : ''}`}
                data-testid="archived-filter"
                onClick={toggleArchivedView}
                title={archivedView ? 'Back to your projects' : 'Show the archived projects: open, unarchive or delete them'}
                type="button"
              >
                Archived ({archivedProjects.length})
              </button>
            )}
          </div>

          <div className="projects-toolbar-group" data-group="sort">
            <select
              aria-label="Sort by"
              className="filter-dropdown projects-sort"
              onChange={(e) => setSort(SORT_PRESETS.find((preset) => preset.id === e.target.value).sort)}
              value={sortPreset ? sortPreset.id : 'column'}
            >
              {SORT_PRESETS.map((preset) => <option key={preset.id} value={preset.id}>Sort: {preset.label}</option>)}
              {!sortPreset && <option disabled value="column">Sort: {sortLabel}</option>}
            </select>
          </div>

          <div aria-label="View" className="projects-toolbar-group projects-view-group" data-group="view" role="group">
            <span className="projects-view-toggle">
              <button
                aria-label="List view"
                aria-pressed={viewMode === 'list'}
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => handleViewModeChange('list')}
                title="List view"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                  <rect x="2" y="3" width="12" height="2" rx="1"/>
                  <rect x="2" y="7" width="12" height="2" rx="1"/>
                  <rect x="2" y="11" width="12" height="2" rx="1"/>
                </svg>
              </button>
              <button
                aria-label="Grid view"
                aria-pressed={viewMode === 'grid'}
                className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => handleViewModeChange('grid')}
                title="Grid view"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                  <rect x="2" y="2" width="5" height="5" rx="1"/>
                  <rect x="9" y="2" width="5" height="5" rx="1"/>
                  <rect x="2" y="9" width="5" height="5" rx="1"/>
                  <rect x="9" y="9" width="5" height="5" rx="1"/>
                </svg>
              </button>
            </span>

            <button
              aria-label="Refresh projects"
              className="refresh-btn"
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh projects"
              type="button"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={loading ? 'spinning' : ''} aria-hidden="true">
                <path d="M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C9.84871 2 11.4822 2.85714 12.5355 4.17157" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M12 1.5V4.5H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {viewMode === 'list' && !archivedView && <ColumnsButton columns={tableColumns} show={tableColumnState.show} toggle={tableColumnState.toggle} />}

            {shownProjects.length > 0 && (
              <button
                type="button"
                className={`select-mode-btn${selectionMode ? ' active' : ''}`}
                onClick={toggleSelectionMode}
              >
                {selectionMode ? 'Cancel' : 'Select'}
              </button>
            )}
          </div>
        </div>

        {!loading && !error && !archivedView && (
          <div className="projects-count" data-testid="projects-count" role="status">
            <span>
              {filtersOn ? `${displayProjects.length} of ${projects.length} projects` : `${projects.length} project${projects.length === 1 ? '' : 's'}`}
            </span>
            {filtersOn && <button className="projects-clear-filters" onClick={clearFilters} type="button">Clear filters</button>}
          </div>
        )}
      </div>

      {!loading && !error && selectionMode && shownProjects.length > 0 && (
        <div className="projects-bulk-toolbar">
          <label className="bulk-select-all">
            <input
              type="checkbox"
              checked={shownProjects.length > 0 && shownProjects.every((p) => selectedIds.has(p._id))}
              onChange={handleSelectAll}
            />
            <span>
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : `Select all (${shownProjects.length})`}
            </span>
          </label>
          {selectedIds.size > 0 && archivedView && (
            <>
              <button className="bulk-clear-btn" onClick={clearSelection} type="button">Clear</button>
              <button className="bulk-clear-btn" disabled={unarchiving} onClick={() => handleUnarchive(Array.from(selectedIds))} type="button">
                {unarchiving ? 'Unarchiving…' : `Unarchive ${selectedIds.size}`}
              </button>
              <button className="bulk-delete-btn" disabled={isBulkDeleting} onClick={handleBulkDeleteClick} type="button">
                {isBulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size}`}
              </button>
            </>
          )}
          {selectedIds.size > 0 && !archivedView && (
            <>
              <button
                type="button"
                className="bulk-clear-btn"
                onClick={clearSelection}
              >
                Clear
              </button>
              <button
                type="button"
                className="bulk-delete-btn"
                onClick={handleBulkDeleteClick}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size}`}
              </button>
              {archiveOn && (
                <button
                  className="bulk-clear-btn"
                  onClick={() => askArchive(displayProjects.filter((p) => selectedIds.has(p._id) && !p.sharedRole))}
                  title="Archive the selected projects of yours (shared ones are left as they are)"
                  type="button"
                >
                  {`Archive ${displayProjects.filter((p) => selectedIds.has(p._id) && !p.sharedRole).length}`}
                </button>
              )}
              {config.sandboxFirst && (
                <>
                  <StartButton
                    className="bulk-start"
                    disabled={Boolean(bulkRun)}
                    label={`Start ${selectedIds.size}`}
                    onDismiss={bulkRun ? undefined : () => setBulkProgress(null)}
                    onRetry={bulkRun ? undefined : retryBulkStart}
                    onStart={() => handleBulkRun('start')}
                    progress={bulkStartProgress}
                    size="middle"
                    type="default"
                  />
                  <button className="bulk-clear-btn" disabled={Boolean(bulkRun)} onClick={() => handleBulkRun('stop')} type="button">
                    {bulkRun === 'stop' ? 'Stopping…' : `Stop ${selectedIds.size}`}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}

      {!loading && !error && sandboxWaking && (
        <SandboxWaking
          compact
          fallback={(
            <div className="projects-sandbox-waking" role="status">
              <div className="spinner"></div>
              <p>Waking your sandbox… these are the projects we know of so far.</p>
            </div>
          )}
          watch
        />
      )}

      {loading && (
        // While the list waits for a sandbox that is starting: what it is doing, how long, Retry (SandboxWaking).
        <SandboxWaking
          fallback={(
            <div className="projects-loading">
              <div className="spinner"></div>
              <p>Loading projects...</p>
            </div>
          )}
          watch
        />
      )}

      {error && (
        <div className="projects-error">
          <p>❌ {error}</p>
          <button onClick={fetchProjects} className="retry-btn">Retry</button>
        </div>
      )}

      {!loading && !error && archivedView && (
        <ArchivedProjectsList
          busy={unarchiving || isBulkDeleting}
          formatDate={formatDate}
          onDelete={handleArchivedDelete}
          onOpen={handleProjectClick}
          onSelectAll={handleSelectAll}
          onSelectToggle={handleSelectToggle}
          onUnarchive={handleUnarchive}
          projects={archivedShown}
          selectedIds={selectedIds}
          selectionMode={selectionMode}
        />
      )}

      {!loading && !error && !archivedView && filtersOn && displayProjects.length === 0 && (
        <div className="projects-empty-favorites" data-testid="projects-no-match">
          <p>
            {favoritesOnly && activeFilterCount(filters) === 1
              ? 'No favorite projects yet. Click the ☆ on a project to add it here.'
              : 'No project matches these filters.'}
          </p>
          <button className="retry-btn" onClick={clearFilters} type="button">Clear filters</button>
        </div>
      )}

      {!loading && !error && !archivedView && viewMode === 'grid' && (
        <div className="projects-grid">
          {/* Create new project card */}
          <div className="project-card create-card" onClick={handleCreateProjectClick}>
            <div className="create-card-content">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="create-icon">
                <rect x="12" y="12" width="24" height="24" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" rx="2"/>
                <path d="M24 18V30M18 24H30" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <p className="create-text">Create new project</p>
            </div>
          </div>

          {displayProjects.map((project) => (
            <ProjectCard
              key={project._id}
              project={project}
              onClick={() => handleProjectClick(project)}
              isFavorite={favorites.has(project._id)}
              onFavoriteToggle={handleFavoriteToggle}
              selectable={selectionMode}
              isSelected={selectedIds.has(project._id)}
              onSelectToggle={handleSelectToggle}
              showIdHint={duplicateNameProjectIds.has(project._id)}
              footer={config.sandboxFirst ? <>{runControls(project._id)}{remixButton(project)}{workspaceButton(project)}{archiveMenu(project)}</> : runControls(project._id)}
            />
          ))}
        </div>
      )}

      {!loading && !error && !archivedView && viewMode === 'list' && !(filtersOn && displayProjects.length === 0) && (
        <DataTable
          actions={{
            label: 'Actions',
            header: <span className="projects-actions-header">Actions</span>,
            width: actionsWidth,
            render: (project) => (
              /* The run buttons, then one fixed slot per icon (Remix, Add to workspace, ⋯), kept even when a row has no
                 such button (a shared project cannot be moved), so every row's buttons end on the same edge. */
              <span className="actions-column">
                <span className="list-actions-run">{runControls(project._id, 'default')}</span>
                {config.sandboxFirst && <span className="list-action-slot">{remixButton(project)}</span>}
                {workspacesOn && <span className="list-action-slot">{workspaceButton(project)}</span>}
                {archiveOn && <span className="list-action-slot">{archiveMenu(project)}</span>}
              </span>
            ),
          }}
          ariaLabel="Projects"
          className="projects-table"
          columnState={tableColumnState}
          columns={tableColumns}
          empty={<span className="projects-table-empty">No projects yet. Create one with New Project.</span>}
          leading={selectionMode ? {
            width: 40,
            header: (
              <input
                aria-label="Select all projects"
                checked={displayProjects.length > 0 && displayProjects.every((p) => selectedIds.has(p._id))}
                onChange={handleSelectAll}
                type="checkbox"
              />
            ),
            render: (project) => (
              <input
                aria-label={`Select ${project.name || 'Untitled Project'}`}
                checked={selectedIds.has(project._id)}
                onChange={() => handleSelectToggle(project._id)}
                onClick={(e) => e.stopPropagation()}
                type="checkbox"
              />
            ),
          } : undefined}
          minWidth={tableMinWidth}
          onSort={setSort}
          rowKey="_id"
          rowProps={(project) => ({
            className: `list-row project-row${selectedIds.has(project._id) ? ' is-selected' : ''}`,
            onClick: () => handleProjectClick(project),
          })}
          rows={displayProjects}
          scrollLabel="Projects table, scrolls sideways"
          serverSort
          sort={sort}
          wrapClassName="projects-table-wrap"
        />
      )}

      {config.sandboxFirst && (
        <NewProjectDialog onClose={() => setRemixOf(null)} open={Boolean(remixOf)} remixOf={remixOf} selectedTenant={selectedTenant} />
      )}

      {/* Create project: in sandbox-first just a name (a skeleton vfs project, then Code); the legacy modal otherwise */}
      {config.sandboxFirst ? (
        <NewProjectDialog
          onClose={() => setIsCreateProjectModalOpen(false)}
          open={isCreateProjectModalOpen}
          selectedTenant={selectedTenant}
        />
      ) : (
        <CreateProjectModal
          isOpen={isCreateProjectModalOpen}
          onClose={() => {
            setIsCreateProjectModalOpen(false);
            setInitialProjectPrompt('');
          }}
          onCreateProject={handleCreateProject}
          selectedTenant={selectedTenant}
          initialPrompt={initialProjectPrompt}
        />
      )}

      {workspacesOn && (
        <AddToWorkspaceDialog
          current={moving ? workspacesOf(moving._id) : []}
          onClose={() => setMoving(null)}
          onSaved={({ added, removed, keepOpen }) => {
            const parts = [added.length ? `Added to ${added.join(', ')}` : '', removed.length ? `Removed from ${removed.join(', ')}` : ''].filter(Boolean);
            if (parts.length) { showToast(parts.join(' · ')); }
            if (moving) { announceWorkspacesChanged(moving._id); }
            if (!keepOpen) { setMoving(null); }
            reloadWorkspaces();
          }}
          open={Boolean(moving)}
          project={moving ? { id: moving._id, name: moving.name } : null}
          workspaces={workspaces}
        />
      )}

      <ArchiveDialog onCancel={() => setArchiving(null)} onDone={onArchived} projects={archiving} />

      <ConfirmDialog
        isOpen={showBulkDeleteConfirm}
        title={`Delete ${selectedIds.size} project${selectedIds.size !== 1 ? 's' : ''}`}
        message={`Are you sure you want to delete ${selectedIds.size} project${selectedIds.size !== 1 ? 's' : ''}? This action cannot be undone.`}
        onConfirm={handleBulkDeleteConfirm}
        onCancel={() => setShowBulkDeleteConfirm(false)}
      />

    </div>
  );
};

export default Projects;
